import React, {useState, useEffect, useMemo} from 'react';
import {API} from 'aws-amplify';
import debounce from 'lodash/debounce';
import {v4 as uuidv4} from 'uuid';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {ENDPOINT} from '../../constants';

let autocompleteFetchPromise; // Used to prevent autocomplete request from stacking up

export default function AutoCompletePlace({
  onSelect,
  initialValue,
  inputLabel = 'Place',
  inputVariant = 'standard',
  isRequired = false,
}) {
  const [place, setPlace] = useState(initialValue); // Value of the selected autocomplete
  const [inputValue, setInputValue] = useState(''); // What is typed in input
  const [options, setOptions] = useState([]); // Autocomplete results to choose from

  useEffect(() => {
    setPlace(initialValue);
  }, [initialValue]);

  const sessionToken = uuidv4();
  const fetchPlaces = (query) => {
    if (autocompleteFetchPromise) API.cancel(autocompleteFetchPromise, 'Google Place autocomplete request canceled');

    const requestBody = {body: {query, sessionToken}};
    const promise = API.post(ENDPOINT, '/google/places/autocomplete', requestBody);
    autocompleteFetchPromise = promise;

    return promise
      .then(setOptions)
      .catch((error) => {
        if (!API.isCancel(error)) console.log(error);
      })
      .finally(() => {
        autocompleteFetchPromise = null;
      });
  };

  // Fetch Google places on input changed
  const fetchPlacesDebounced = useMemo(() => debounce(fetchPlaces, 200), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (inputValue === '') {
      setOptions(place ? [place] : []);
      return undefined;
    }

    fetchPlacesDebounced(inputValue);

    return function cleanup() {
      if (autocompleteFetchPromise) API.cancel(autocompleteFetchPromise, 'Google Place autocomplete request canceled');
      autocompleteFetchPromise = null;
    }
  }, [inputValue, place, fetchPlacesDebounced]);

  function handleInputValueChanged(event, newValue) {
    setInputValue(newValue);
  }

  async function handlePlaceChange(event, newValue) {
    setOptions(newValue ? [newValue, ...options] : options);
    setPlace(newValue);
    onSelect(newValue);
  }

  function renderOption(props, option) {
    return (
      <li {...props}>
        <Grid container alignItems="center">
          <Grid item>
            <LocationOnIcon className="m-r-10" />
          </Grid>
          <Grid item xs>
            <span>{option.name}</span>

            <Typography variant="body2" color="textSecondary">
              {option.place}
            </Typography>
          </Grid>
        </Grid>
      </li>
    );
  }

  return (
    <Autocomplete
      getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.name}, ${option.place}`)}
      filterOptions={(x) => x}
      options={options}
      renderOption={renderOption}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={place}
      onChange={handlePlaceChange}
      inputValue={inputValue}
      onInputChange={handleInputValueChanged}
      renderInput={(params) => (
        <TextField {...params} variant={inputVariant} label={inputLabel} fullWidth required={isRequired} />
      )}
      isOptionEqualToValue={(option, value) => `${option.name}, ${option.place}` === `${value.name}, ${value.place}`}
    />
  );
}
